import "../components/feedbackview.css";
import { Interview } from "../models/Interview";
import AudioPlayerComponent from "../components/AudioPlayerComponent";
import TranscriptComponent from "../components/TranscriptComponent";
import { useEffect, useRef, useState } from "react";
import { QuestionAnalysis } from "../models/QuestionAnalysis";
import ConfirmationModal from "../components/ConfirmationModal";
import {
  fetchQuestionAnalysisList,
  saveQuestionAnalysisList,
} from "../services/QuestionAnalysisService";
import { editInterview, getInterviewById } from "../services/InterviewService";
import QuestionAnalysisForm from "../components/QuestionAnalysisForm";
import QuestionListComponent from "../components/QuestionListComponent";
import ConsentComponent from "../components/ConsentComponent";
import { useSearchParams } from "react-router-dom";
import { fetchWithAuthentication } from "../utilities/FetchUtil";
import { convertToLocalDateTime } from "../utilities/DateUtil";
import { Button, Flex, Group, Stack, Text, Title } from "@mantine/core";
import { useBeforeUnload } from "../hooks/useBeforeUnload";

const FeedbackPage = () => {
  const [searchParams] = useSearchParams();

  const [interview, setInterview] = useState<Interview>();
  const [questionAnalysisList, setQuestionAnalysisList] = useState<
    QuestionAnalysis[]
  >([]);
  const [questionAnalysis, setQuestionAnalysis] = useState<QuestionAnalysis>({
    overallScore: 0,
    onPointScore: 0,
    conciseScore: 0,
    deliveryScore: 0,
    userAnswer: "",
    onPointFeedback: "",
    conciseFeedback: "",
    deliveryFeedback: "",
    alternativeAnswer: "",
    questionNum: 1,
    question: "",
    interviewId: "",
  });
  const [currentlyEditing, setCurrentlyEditing] = useState<boolean>(false);
  const [edited, setEdited] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [unsavedChanges, setUnsavedChanges] = useState<boolean>(false);
  const [deletedQuestions, setDeletedQuestions] = useState<QuestionAnalysis[]>(
    [],
  );
  const [itemToDelete, setItemToDelete] = useState<QuestionAnalysis>();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
  const [showSubmitAllModal, setShowSubmitAllModal] = useState<boolean>(false);
  const [showFeedbackSubmittedModal, setShowFeedbackSubmittedModal] =
    useState<boolean>(false);
  const [grantedConsent, setGrantedConsent] = useState<boolean>(false);

  useBeforeUnload(unsavedChanges);

  useEffect(() => {
    const fetchInterview = async () => {
      console.log(searchParams.get("interviewId"));
      const response = await fetchWithAuthentication(
        `/interview?interviewId=${searchParams.get("interviewId")}`,
        "GET",
        null,
      );
      return response.json();
    };
    const fetchQuestionAnalysisData = async (fetchedInterview: Interview) => {
      try {
        return await fetchQuestionAnalysisList(
          fetchedInterview.interviewId,
          setLoading,
        );
      } catch (error: any) {
        console.error(error.message);
      }
    };

    fetchInterview().then((interview) => {
      setInterview(interview);
      resetQuestionAnalysis(interview.interviewId);
      fetchQuestionAnalysisData(interview).then((questionAnalysisList) => {
        setQuestionAnalysisList(questionAnalysisList);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetQuestionAnalysis = (interviewId: string) => {
    setQuestionAnalysis({
      overallScore: 0,
      onPointScore: 0,
      conciseScore: 0,
      deliveryScore: 0,
      userAnswer: "",
      onPointFeedback: "",
      conciseFeedback: "",
      deliveryFeedback: "",
      alternativeAnswer: "",
      questionNum: questionAnalysisList.length + 2,
      question: "",
      interviewId: interviewId,
    });
  };

  const handleAddAnalysis = () => {
    const overallScore: number =
      (Number(questionAnalysis.onPointScore) +
        Number(questionAnalysis.conciseScore) +
        Number(questionAnalysis.deliveryScore)) /
      3;

    const updatedAnalysis = {
      ...questionAnalysis,
      overallScore: Number(overallScore.toFixed(1)),
      questionNum: questionAnalysisList.length + 1,
    };

    setQuestionAnalysisList((prevState) => [...prevState, updatedAnalysis]);
    resetQuestionAnalysis(interview!.interviewId);
    setEdited(false);
    setUnsavedChanges(true);
  };

  const handleDelete = (analysis: QuestionAnalysis) => {
    setEdited(true);
    setDeletedQuestions((prevState) => [...prevState, analysis]);

    const updatedList = questionAnalysisList.filter(
      (item) => item.questionNum !== analysis.questionNum,
    );
    let count = 1;
    updatedList.forEach((item) => {
      item.questionNum = count++;
    });

    setQuestionAnalysisList(updatedList);
    resetQuestionAnalysis(interview!.interviewId);
    setCurrentlyEditing(false);
    setShowDeleteModal(false);
    setUnsavedChanges(true);
  };

  const handleEdit = (analysis: QuestionAnalysis) => {
    setCurrentlyEditing(true);
    setEdited(true);
    setQuestionAnalysis(analysis);
  };

  const handleDoneEditing = (updatedAnalysis: QuestionAnalysis) => {
    const overallScore: number =
      (Number(updatedAnalysis.onPointScore) +
        Number(updatedAnalysis.conciseScore) +
        Number(updatedAnalysis.deliveryScore)) /
      3;

    setQuestionAnalysisList((prevItems) =>
      prevItems.map((item) =>
        item.questionNum === updatedAnalysis.questionNum
          ? {
              ...item,
              question: updatedAnalysis.question,
              overallScore: Number(overallScore.toFixed(1)),
              onPointScore: updatedAnalysis.onPointScore,
              conciseScore: updatedAnalysis.conciseScore,
              deliveryScore: updatedAnalysis.deliveryScore,
              userAnswer: updatedAnalysis.userAnswer,
              onPointFeedback: updatedAnalysis.onPointFeedback,
              conciseFeedback: updatedAnalysis.conciseFeedback,
              deliveryFeedback: updatedAnalysis.deliveryFeedback,
              alternativeAnswer: updatedAnalysis.alternativeAnswer,
            }
          : item,
      ),
    );

    resetQuestionAnalysis(interview!.interviewId);
    setCurrentlyEditing(false);
    setUnsavedChanges(true);
  };

  const saveQuestionAnalysisData = async (
    method: string,
    list: QuestionAnalysis[],
  ) => {
    try {
      await saveQuestionAnalysisList(
        method,
        list,
        questionAnalysisList,
        setQuestionAnalysisList,
      );
      setUnsavedChanges(false);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleSaveProgress = () => {
    let arrayWithIds: QuestionAnalysis[] = [];
    let arrayWithoutIds: QuestionAnalysis[] = [];

    questionAnalysisList.forEach((item) => {
      if (item.questionAnalysisId) {
        arrayWithIds.push(item);
      } else {
        arrayWithoutIds.push(item);
      }
    });

    deletedQuestions.forEach((deletedQuestion, index) => {
      if (!deletedQuestion.questionAnalysisId) {
        deletedQuestions.splice(index, 1);
      }
    });

    if (deletedQuestions.length !== 0) {
      saveQuestionAnalysisData("DELETE", deletedQuestions);
    }

    if (arrayWithoutIds.length !== 0) {
      saveQuestionAnalysisData("POST", arrayWithoutIds);
    }

    if (arrayWithIds.length !== 0) {
      saveQuestionAnalysisData("PUT", arrayWithIds);
    }

    setUnsavedChanges(false);
    setEdited(false);
    setShowSaveModal(true);
  };

  const submitInterview = async (interviewToSubmit: Interview) => {
    try {
      return await editInterview(interviewToSubmit);
    } catch (error: any) {
      console.error(error.message);
    }
  };

  const handleSubmitAll = () => {
    setShowSubmitAllModal(false);
    // @ts-ignore
    setInterview((prevState) => ({
      ...prevState,
      consentGranted: grantedConsent,
      feedbackSubmitted: true,
    }));
  };

  const prevFeedbackSubmittedRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (!interview) return; // Ensure interview exists
    const hasFeedbackChanged =
      prevFeedbackSubmittedRef.current === false && interview.feedbackSubmitted;
    prevFeedbackSubmittedRef.current = interview.feedbackSubmitted; // Update reference AFTER update

    if (!hasFeedbackChanged) return; // Only proceed when feedback changes from false → true

    const submitAndUpdate = async () => {
      try {
        await submitInterview(interview);
        const updatedInterview = await getInterviewById(interview.interviewId);
        setInterview(updatedInterview);
        setShowFeedbackSubmittedModal(true);
      } catch (error) {
        console.error("Failed to submit interview:", error);
      }
    };

    submitAndUpdate();
  }, [interview]);

  const normalizeUrl = (url: string) => {
    if (!/^https?:\/\//i.test(url)) {
      return "https://" + url;
    }
    return url;
  };

  const verifyLinkedInWasUploaded = (linkedInUrl: string) => {
    if (linkedInUrl === "https://www.linkedin.com/in/") {
      return "";
    } else {
      return linkedInUrl;
    }
  };

  const downloadResumeText = (interview: Interview) => {
    if (interview.resumeText !== null && interview.resumeText.trim() !== "") {
      const blob = new Blob([interview.resumeText], { type: "text/plain" });
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `resume_${interview.interviewId}.txt`;
      link.click();

      URL.revokeObjectURL(url);
    }
  };

  const title = () => {
    if (interview?.reviewer) {
      return `View Feedback (submitted by ${interview.reviewer.username} at ${convertToLocalDateTime(interview.updatedAt)})`;
    } else {
      return "Give Feedback";
    }
  };

  return (
    <>
      <ConfirmationModal
        opened={showDeleteModal && itemToDelete != null}
        title="Delete question"
        text={
          "Are you sure you want to delete question " +
          itemToDelete?.questionNum +
          "?"
        }
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => {
          handleDelete(itemToDelete!);
        }}
        onCancel={() => setShowDeleteModal(false)}
      />
      <ConfirmationModal
        opened={showSaveModal}
        title="Saved"
        text="Progress saved successfully."
        confirmText="Ok"
        onConfirm={() => {
          setShowSaveModal(false);
          setUnsavedChanges(false);
        }}
        onCancel={() => {
          setShowSaveModal(false);
          setUnsavedChanges(false);
        }}
      />
      <ConfirmationModal
        opened={showSubmitAllModal}
        title="Submit feedback"
        text={
          !interview?.twoPartyConsent
            ? "Are you sure you want to submit all " +
              "feedback to the end user? Once submitted, feedback WILL be viewable by" +
              " the user who uploaded the interview. This cannot be undone."
            : grantedConsent
              ? "You have selected that consent was granted. Are you " +
                "sure you want to submit all feedback to the end user? Once " +
                "submitted, feedback WILL be viewable by the user who uploaded the " +
                "interview. This cannot be undone."
              : "You have selected that consent" +
                " was NOT granted. With no consent, feedback will NOT be available to" +
                " the end user. Are you sure you want to submit? This cannot be undone."
        }
        confirmText="Yes"
        cancelText="No"
        onConfirm={() => handleSubmitAll()}
        onCancel={() => setShowSubmitAllModal(false)}
      />
      <ConfirmationModal
        opened={showFeedbackSubmittedModal}
        title="Feedback submitted"
        text="Feedback submitted to user successfully. You can now close this tab."
        confirmText="Close"
        onConfirm={() => {
          window.close();
        }}
        onCancel={() => {
          window.close();
        }}
      />
      {interview && (
        <Flex direction="column" px="lg" py="sm" h="100vh">
          <Title order={3}>{title()}</Title>
          <Group gap="xs">
            <Title order={4}>{interview.jobTitle}&nbsp;</Title>
            at
            <Title order={4}>&nbsp;{interview.companyName}</Title>
          </Group>
          <Text>Interview submitted by user: {interview.userId}</Text>
          <Text>{interview.createdAt}</Text>

          <Flex h="100%" justify="space-between">
            <QuestionAnalysisForm
              questionAnalysis={questionAnalysis}
              setQuestionAnalysis={setQuestionAnalysis}
              setEdited={setEdited}
              currentlyEditing={currentlyEditing}
              handleDoneEditing={handleDoneEditing}
              handleAddAnalysis={handleAddAnalysis}
              interviewSubmitted={interview.feedbackSubmitted}
            />
            <Stack justify="space-between" w="30%" px="md">
              <QuestionListComponent
                loading={loading}
                questionAnalysisList={questionAnalysisList}
                handleEdit={handleEdit}
                setItemToDelete={setItemToDelete}
                setShowDeleteModal={setShowDeleteModal}
                interviewSubmitted={interview.feedbackSubmitted}
              />
              <Stack gap="sm">
                <Title order={4} mb="md">
                  Links
                </Title>
                <Button
                  onClick={() => window.open(normalizeUrl(interview.jobUrl))}
                  disabled={interview.jobUrl === ""}
                >
                  {interview.jobUrl !== ""
                    ? "Job posting"
                    : "No job URL uploaded"}
                </Button>
                <Button
                  onClick={() =>
                    window.open(normalizeUrl(interview.linkedinUrl))
                  }
                  disabled={
                    verifyLinkedInWasUploaded(interview.linkedinUrl) === ""
                  }
                >
                  {verifyLinkedInWasUploaded(interview.linkedinUrl) !== ""
                    ? "Applicant LinkedIn"
                    : "No LinkedIn URL uploaded"}
                </Button>
                <Button
                  onClick={() => downloadResumeText(interview)}
                  disabled={
                    interview.resumeText === null ||
                    interview.resumeText === "" ||
                    interview.resumeText.trim() === ""
                  }
                >
                  {interview.resumeText !== null &&
                  interview.resumeText.trim() !== ""
                    ? "Resume text"
                    : "No resume uploaded"}
                </Button>
              </Stack>
            </Stack>
            <Flex h="100%" direction="column" w="30%" justify="space-between">
              <Title order={4}>Audio:</Title>
              <AudioPlayerComponent interview={interview} />
              <Title order={4} mt="md">
                Transcript:
              </Title>
              <TranscriptComponent
                interview={interview}
                height={interview.twoPartyConsent ? "200px" : "400px"}
              />
              {!interview.feedbackSubmitted && interview.twoPartyConsent ? (
                <ConsentComponent
                  grantedConsent={grantedConsent}
                  setGrantedConsent={setGrantedConsent}
                />
              ) : null}
              {!interview.feedbackSubmitted && (
                <Group mt="md" justify="flex-end">
                  <Button size="lg" onClick={handleSaveProgress}>
                    Save Progress
                  </Button>
                  <Button
                    size="lg"
                    onClick={() => setShowSubmitAllModal(true)}
                    disabled={
                      questionAnalysisList.length === 0 ||
                      edited ||
                      !!questionAnalysisList.find(
                        (item) => !item.questionAnalysisId,
                      )
                    }
                  >
                    Submit All
                  </Button>
                </Group>
              )}
            </Flex>
          </Flex>
        </Flex>
      )}
    </>
  );
};

export default FeedbackPage;
